import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { SuiteContextProvider, SuiteContextSync, CommandPalette } from "@/foundation/context";
import ProtectedRoute from "@/components/ProtectedRoute";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import AcceptInvite from "@/pages/AcceptInvite";
import Wizard from "@/pages/Wizard";
import Dashboard from "@/pages/Dashboard";
import Companies from "@/pages/Companies";
import CompanyEditPage from "@/pages/CompanyEditPage";
import Employees from "@/pages/Employees";
import EmployeeEditPage from "@/pages/EmployeeEditPage";
import Scadenze81 from "@/pages/Scadenze81"; // eslint-disable-line no-unused-vars -- Prechiusura: escluso dal runtime
import HourProfiles from "@/pages/HourProfiles";
import CompilaOrari from "@/pages/CompilaOrari";
import Attendance from "@/pages/Attendance";
import ImportExcel from "@/pages/ImportExcel";
import ImportTabulato from "@/pages/ImportTabulato";
import ImportCartellino from "@/pages/ImportCartellino";
import { PersonDossier } from "@/foundation/identity";
import Reconciliation from "@/pages/Reconciliation";
import CounterConsolidationAdmin from "@/pages/CounterConsolidationAdmin";
import Reports from "@/pages/Reports";
import ModalitaGuidata from "@/pages/ModalitaGuidata";
import Impostazioni from "@/pages/Impostazioni";
import ModuloPlaceholder from "@/pages/ModuloPlaceholder";
import EmployeeDashboard from "@/pages/EmployeeDashboard";
import EmployeeLeaves from "@/pages/EmployeeLeaves";
import LeaveRequestsInbox from "@/pages/LeaveRequestsInbox";
import LeaveCalendar from "@/pages/LeaveCalendar";
import HelpCenter from "@/pages/HelpCenter";
import ChangePasswordGate from "@/components/auth/ChangePasswordGate";
import IdleSessionProvider from "@/contexts/IdleSessionContext";
import { ActiveCompanyProvider } from "@/contexts/ActiveCompanyContext";
import { DurationFormatProvider } from "@/foundation/duration";
import { useEffectiveRole } from "@/foundation/context";

function HomeSwitch() {
  // Prospettiva UI: la Home iniziale segue il Context Engine (ruolo effettivo).
  // L'accesso alle route resta invece regolato da ProtectedRoute + ruolo reale.
  const role = useEffectiveRole();
  if (role === "employee") return <EmployeeDashboard />;
  if (role === "responsabile") return <Navigate to="/impostazioni" replace />;
  return <Dashboard />;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/login/:slug" element={<Login />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/accept-invite" element={<AcceptInvite />} />
      <Route
        path="/wizard"
        element={
          <ProtectedRoute roles={["admin"]}>
            <div className="min-h-screen bg-slate-50 p-4 md:p-8" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
              <Wizard />
            </div>
          </ProtectedRoute>
        }
      />
      <Route
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route index element={<HomeSwitch />} />
        <Route path="aziende" element={<ProtectedRoute roles={["admin", "super_admin", "client", "company_admin", "company_responsible", "external_consultant"]}><Companies /></ProtectedRoute>} />
        <Route path="aziende/:id" element={<ProtectedRoute roles={["admin", "super_admin", "client", "company_admin", "company_responsible", "external_consultant"]}><CompanyEditPage /></ProtectedRoute>} />
        <Route path="dipendenti" element={<ProtectedRoute roles={["admin", "client", "external_consultant"]}><Employees /></ProtectedRoute>} />
        <Route path="dipendenti/:id" element={<ProtectedRoute roles={["admin", "client", "external_consultant", "employee"]}><EmployeeEditPage /></ProtectedRoute>} />
        {/* Prechiusura release - route esclusa (Scadenze81 non approvate) */}
        <Route path="mio-profilo" element={<ProtectedRoute roles={["employee"]}><Employees /></ProtectedRoute>} />
        <Route path="profili-orari" element={<ProtectedRoute roles={["admin", "super_admin", "client", "studio_operator", "company_admin", "company_responsible"]}><HourProfiles /></ProtectedRoute>} />
        <Route path="presenze" element={<Attendance />} />
        <Route path="presenze/compila-orari" element={<CompilaOrari />} />
        <Route path="import-excel" element={<ProtectedRoute roles={["admin"]}><ImportExcel /></ProtectedRoute>} />
        <Route path="import-tabulato" element={<ProtectedRoute roles={["admin"]}><ImportTabulato /></ProtectedRoute>} />
        <Route path="import-cartellino" element={<ProtectedRoute roles={["admin", "client"]}><ImportCartellino /></ProtectedRoute>} />
        <Route path="riconciliazione" element={<ProtectedRoute roles={["admin"]}><Reconciliation /></ProtectedRoute>} />
        <Route path="consolidamento-contatori" element={<ProtectedRoute roles={["admin"]}><CounterConsolidationAdmin /></ProtectedRoute>} />
        <Route path="guidata" element={<ProtectedRoute roles={["admin", "client"]}><ModalitaGuidata /></ProtectedRoute>} />
        <Route path="calendario-ferie" element={<ProtectedRoute roles={["admin", "client", "external_consultant"]}><LeaveCalendar /></ProtectedRoute>} />
        <Route path="assenze-permessi" element={<ProtectedRoute roles={["employee"]}><EmployeeLeaves /></ProtectedRoute>} />
        <Route path="richieste-ferie" element={<ProtectedRoute roles={["admin", "super_admin", "root", "client", "company_admin", "company_responsible", "studio_operator", "responsabile", "external_consultant"]}><LeaveRequestsInbox /></ProtectedRoute>} />
        <Route path="guida" element={<HelpCenter />} />
        <Route
          path="impostazioni"
          element={
            <ProtectedRoute
              roles={[
                "admin",
                "super_admin",
                "client",
                "company_admin",
                "responsabile",
                "company_responsible",
                "studio_operator",
              ]}
            >
              <Impostazioni />
            </ProtectedRoute>
          }
        />
        <Route path="report" element={<Reports />} />
        {/* v1.5.7 · INT 1 — Il modulo "presenze" non ha landing descrittiva: entra sulla Dashboard operativa. */}
        <Route path="moduli/presenze" element={<Navigate to="/" replace />} />
        <Route path="moduli/:slug" element={<ModuloPlaceholder />} />
        {/* v1.5.0 · Foundation Identity: Fascicolo Persona */}
        <Route
          path="persone/:id"
          element={
            <ProtectedRoute
              roles={["admin", "super_admin", "client", "company_admin",
                        "company_responsible", "responsabile", "studio_operator"]}
            >
              <PersonDossier />
            </ProtectedRoute>
          }
        />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <IdleSessionProvider>
            <ActiveCompanyProvider>
              <SuiteContextProvider>
                <SuiteContextSync />
                <ChangePasswordGate>
                  <DurationFormatProvider>
                    <AppRoutes />
                  </DurationFormatProvider>
                </ChangePasswordGate>
                <Toaster richColors position="top-right" />
                <CommandPalette />
              </SuiteContextProvider>
            </ActiveCompanyProvider>
          </IdleSessionProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
